import useTerms from "../../../hooks/useTerms";
import React, { useEffect, useState } from "react";
import { Box, MenuItem } from "@mui/material";
import { Inventory2Outlined as PackageIcon } from "@mui/icons-material";
import { DrawerField } from "../../molecules";
import { FormCheckbox, FormSelect } from "../../atoms";
import { useClinicTheme } from "../../../theme";
import { formatMoney, kurusToInput, parseMoneyInput } from "../../../utils/money";
import { DrawerInput, FormDrawer, fieldGridSx } from "./billingParts";

const EMPTY = { name: "", serviceId: "", sessionCount: "10", price: "", validityDays: "90", isActive: true };

const intIn = (v, min, max) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max;
};

/**
 * Paket şablonu ("10 seans hacamat · 90 gün") oluşturma / düzenleme.
 * Şablon değişikliği satılmış paketleri etkilemez (satışta anlık kopya alınır).
 */
export default function PackageTemplateEditorDrawer({ open, onClose, template, services = [], onSave, saving }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    setForm(
      template
        ? {
            name: template.name || "",
            serviceId: String(template.service?._id || template.service || ""),
            sessionCount: String(template.sessionCount || ""),
            price: kurusToInput(template.price),
            validityDays: String(template.validityDays || ""),
            isActive: template.isActive !== false,
          }
        : EMPTY
    );
  }, [open, template]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const price = parseMoneyInput(form.price);
  const service = services.find((s) => String(s._id) === String(form.serviceId));
  const countOk = intIn(form.sessionCount, 1, 365);
  const daysOk = intIn(form.validityDays, 1, 3650);
  const valid = form.name.trim() && form.serviceId && countOk && daysOk && price !== null;
  const perSession = price !== null && countOk ? Math.round(price / Number(form.sessionCount)) : null;

  // Pasif hizmet, şablonun mevcut hizmetiyse listede kalsın
  const options = services.filter((s) => s.isActive !== false || String(s._id) === String(form.serviceId));

  const submit = () =>
    valid &&
    onSave({
      ...(template ? { templateId: template._id } : {}),
      name: form.name.trim(),
      serviceId: form.serviceId,
      sessionCount: Number(form.sessionCount),
      price,
      validityDays: Number(form.validityDays),
      ...(template ? { isActive: form.isActive } : {}),
    });

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      icon={<PackageIcon />}
      title={template ? "Paketi düzenle" : "Yeni paket"}
      subtitle={`${terms.patientDat} satılan seans paketi. Satılmış paketler değişmez.`}
      onSubmit={submit}
      submitLabel={template ? "Kaydet" : "Ekle"}
      loading={saving}
      disabled={!valid}
    >
      <Box sx={fieldGridSx}>
        <DrawerField label="Paket adı *" fullWidth>
          <DrawerInput
            palette={palette}
            autoFocus
            placeholder="Ör. 10 seans hacamat"
            value={form.name}
            onChange={set("name")}
            inputProps={{ maxLength: 120, "aria-label": "Paket adı" }}
          />
        </DrawerField>
        <DrawerField
          label="Hizmet *"
          fullWidth
          hint={options.length === 0 ? "Önce Hizmetler listesine bir hizmet ekleyin." : undefined}
        >
          <FormSelect
            palette={palette}
            variant="standard"
            value={form.serviceId}
            onChange={(v) => setForm((f) => ({ ...f, serviceId: v }))}
            placeholder={null}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>
              Hizmet seçin
            </MenuItem>
            {options.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                {s.name} · {s.durationMin} dk · {formatMoney(s.price)}
              </MenuItem>
            ))}
          </FormSelect>
        </DrawerField>
        <DrawerField label="Seans sayısı *" hint={form.sessionCount && !countOk ? "1–365 arası." : undefined}>
          <DrawerInput
            palette={palette}
            type="number"
            value={form.sessionCount}
            onChange={set("sessionCount")}
            inputProps={{ min: 1, max: 365, "aria-label": "Seans sayısı" }}
          />
        </DrawerField>
        <DrawerField label="Geçerlilik (gün) *" hint={form.validityDays && !daysOk ? "1–3650 arası." : undefined}>
          <DrawerInput
            palette={palette}
            type="number"
            value={form.validityDays}
            onChange={set("validityDays")}
            inputProps={{ min: 1, max: 3650, "aria-label": "Geçerlilik (gün)" }}
          />
        </DrawerField>
        <DrawerField
          label="Paket fiyatı (₺) *"
          fullWidth
          hint={
            form.price && price === null
              ? "Geçerli bir tutar girin (ör. 4.500,00)."
              : perSession !== null && service
                ? `Seans başı ${formatMoney(perSession)} · tek seans fiyatı ${formatMoney(service.price)}`
                : undefined
          }
        >
          <DrawerInput
            palette={palette}
            placeholder="0,00"
            value={form.price}
            onChange={set("price")}
            inputProps={{ inputMode: "decimal", "aria-label": "Paket fiyatı" }}
          />
        </DrawerField>
        {template ? (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <FormCheckbox
              palette={palette}
              label="Satışta (kapalıysa yeni satış yapılamaz; satılmış paketler sürer)"
              checked={form.isActive}
              onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
            />
          </Box>
        ) : null}
      </Box>
    </FormDrawer>
  );
}
